import { useState, useEffect, FormEvent } from 'react';
import { KeyRound, Eye, EyeOff, AlertTriangle, User, CheckCircle2, Circle, LogOut, LockKeyhole } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../api/auth';
import { getApiErrorMessage } from '../api';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '../components/ui/SharedLayout';
import { showToast } from '../components/ui/SharedLayout';

const REQUIREMENTS = [
  {
    id: 'len',
    label: 'Mínimo de 6 caracteres',
    test: (p: string) => p.length >= 6,
  },
  {
    id: 'letter',
    label: 'Pelo menos 1 letra',
    test: (p: string) => /[A-Za-zÀ-ÿ]/.test(p),
  },
  {
    id: 'number',
    label: 'Pelo menos 1 número',
    test: (p: string) => /[0-9]/.test(p),
  },
];

const ChangePasswordRequired = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user && !user.must_change_password) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newPassword || !confirmPassword) {
      const msg = 'Preencha ambos os campos de senha.';
      setError(msg);
      showToast({ type: 'error', title: 'Campos obrigatórios', description: msg });
      return;
    }

    for (const req of REQUIREMENTS) {
      if (!req.test(newPassword)) {
        const msg = `Requisito não atendido: ${req.label}`;
        setError(msg);
        showToast({ type: 'warning', title: 'Ajuste sua senha', description: msg });
        return;
      }
    }

    if (newPassword !== confirmPassword) {
      const msg = 'A confirmação da senha não confere.';
      setError(msg);
      showToast({ type: 'error', title: 'Senhas diferentes', description: msg });
      return;
    }

    setLoading(true);
    try {
      await auth.forceChangePassword({ newPassword });
      setSuccess(true);
      showToast({
        type: 'success',
        title: 'Senha atualizada',
        description: 'Senha alterada com sucesso. Você será redirecionado para o login.',
      });
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1200);
    } catch (err: unknown) {
      const rawMessage = getApiErrorMessage(err, '');
      let title = 'Não foi possível alterar a senha';
      let description =
        rawMessage ||
        'Verifique se a senha não é igual à anterior e atende aos requisitos.';

      if (/mesma|igual|anterior|hist[ií]rico|reutiliz/i.test(rawMessage || '')) {
        title = 'Senha já utilizada';
        description = 'Escolha uma senha diferente das últimas usadas.';
      } else if (/j[aá] usada|repetida|password.*already/i.test(rawMessage || '')) {
        title = 'Senha repetida';
        description = 'Essa senha já foi usada recentemente. Crie uma nova.';
      } else if (/fraca|complex|for[çc]a|m[ií]nima/i.test(rawMessage || '')) {
        title = 'Senha fraca';
        description = rawMessage || 'Sua senha não atende aos requisitos mínimos de segurança.';
      } else if (/[ií]ndice|index|forbidden|403|422|400/i.test(rawMessage || '') && !description) {
        description = 'Ocorreu um erro ao validar sua senha. Tente novamente em instantes.';
      }

      setError(description);
      showToast({ type: 'error', title, description });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-100 via-amber-50/80 to-orange-100/90 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-2xl border border-amber-200/50 bg-white shadow-[0_20px_60px_-15px_rgba(234,88,12,0.25),0_0_0_1px_rgba(255,255,255,0.6)_inset] overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-orange-600 px-7 py-6 text-white">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-inset ring-white/25 shadow-[0_6px_18px_-8px_rgba(120,53,15,0.6)]">
              <AlertTriangle className="h-6 w-6 stroke-[1.9]" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Troca de senha obrigatória</h1>
              <p className="mt-1 text-sm leading-relaxed text-amber-50/95">
                O administrador criou ou redefiniu seu acesso. Escolha uma senha definitiva antes
                de usar o sistema.
              </p>
            </div>
          </div>
        </div>

        <div className="px-7 py-7">
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 via-blue-50/60 to-white p-4 text-sm text-blue-900">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <User className="h-4.5 w-4.5 stroke-[1.9]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-blue-900">{user?.name ?? 'Usuário'}</p>
              {user?.email ? <p className="mt-0.5 text-xs text-blue-800/85">{user.email}</p> : null}
            </div>
          </div>

          {success ? (
            <div className="rounded-xl border border-emerald-200 bg-gradient-to-b from-emerald-50 to-white p-6 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-14 w-14 text-emerald-600 stroke-[1.8]" />
              <p className="font-bold text-emerald-800">Senha atualizada com sucesso.</p>
              <p className="mt-1 text-sm text-emerald-700">
                Você será redirecionado para o login em instantes...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="new-password" className="font-semibold">
                  <LockKeyhole className="h-4 w-4" /> Nova senha
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="new-password"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Crie sua nova senha"
                    autoComplete="new-password"
                    required
                    disabled={loading}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-slate-500 hover:text-slate-700 focus:outline-none disabled:opacity-60"
                    aria-label={showNew ? 'Ocultar senha' : 'Mostrar senha'}
                    disabled={loading}
                  >
                    {showNew ? <EyeOff size={18} strokeWidth={1.9} /> : <Eye size={18} strokeWidth={1.9} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-password" className="font-semibold">
                  <KeyRound className="h-4 w-4" /> Repetir nova senha
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a nova senha"
                    autoComplete="new-password"
                    required
                    disabled={loading}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-slate-500 hover:text-slate-700 focus:outline-none disabled:opacity-60"
                    aria-label={showConfirm ? 'Ocultar confirmação' : 'Mostrar confirmação'}
                    disabled={loading}
                  >
                    {showConfirm ? <EyeOff size={18} strokeWidth={1.9} /> : <Eye size={18} strokeWidth={1.9} />}
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50/80 to-white p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">
                  Requisitos de complexidade:
                </p>
                <ul className="space-y-1.5">
                  {REQUIREMENTS.map((req) => {
                    const ok = req.test(newPassword);
                    return (
                      <li
                        key={req.id}
                        className={`text-xs flex items-center gap-2 ${ok ? 'text-emerald-700' : 'text-slate-600'}`}
                      >
                        {ok ? (
                          <CheckCircle2 className="h-4 w-4 stroke-[2.1]" />
                        ) : (
                          <Circle className="h-3 w-3 fill-slate-200 text-slate-300" strokeWidth={0} />
                        )}
                        <span className="leading-snug">{req.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                  {error}
                </div>
              )}

              <div className="flex flex-col-reverse gap-2.5 pt-1 sm:flex-row sm:gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void logout()}
                  disabled={loading}
                  className="sm:flex-1"
                >
                  <LogOut className="h-4 w-4" /> Sair
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="sm:flex-[1.3] !bg-gradient-to-r !from-amber-500 !to-orange-500 hover:!from-amber-600 hover:!to-orange-600 focus:!ring-orange-300"
                >
                  {loading ? 'Atualizando...' : 'Confirmar nova senha'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordRequired;
